/**
 * Get the products to display in the slider
 * 
 * Priority logic:
 * - relatedProductsNoCrops from ANY source (direct or inner) takes priority
 * - Only fallback to relatedProducts if NO relatedProductsNoCrops exists anywhere
 * 
 * @param products - The products definition array (current product's own definition)
 * @param innerRelated - The inner related products (parent product that contains current product)
 * @param allProducts - Fallback products
 * @param currentProductId - Current product ID to filter out
 * @returns Object with sliderProducts and fallbackProducts
 */
export function getSliderProducts(
	products: any,
	innerRelated: any,
	allProducts: any,
	currentProductId?: string
) {
	// Get products from direct definition (products prop - current product's own definition)
	const directNoCrops = products?.[0]?.relatedProductsNoCrops || [];
	const directRelated = products?.[0]?.relatedProducts || [];
	
	// Get products from innerRelated (parent product that has current product in its related list)
	const innerNoCrops = innerRelated?.[0]?.relatedProductsNoCrops || [];
	const innerRelatedProducts = innerRelated?.[0]?.relatedProducts || [];
	
	let sliderProducts: any[] = [];
	let source = "none";
	
	// PRIORITY: Check ALL noCrops sources FIRST before falling back to relatedProducts
	// This ensures relatedProductsNoCrops from anywhere takes priority
	
	// Priority 1: Direct relatedProductsNoCrops (current product's own noCrops)
	if (directNoCrops.length > 0) {
		sliderProducts = directNoCrops;
		source = "directNoCrops";
	}
	// Priority 2: Inner relatedProductsNoCrops (parent's noCrops)
	else if (innerNoCrops.length > 0) {
		sliderProducts = innerNoCrops.filter((x: any) => x.id !== currentProductId);
		source = "innerNoCrops";
	}
	// Priority 3: Direct relatedProducts (current product's own related)
	else if (directRelated.length > 0) {
		sliderProducts = directRelated;
		source = "directRelated";
	}
	// Priority 4: Inner relatedProducts (parent's related)
	else if (innerRelatedProducts.length > 0) {
		sliderProducts = innerRelatedProducts.filter((x: any) => x.id !== currentProductId);
		source = "innerRelated";
	}
	
	// Build fallback array from innerRelated if sliderProducts is empty
	let fallbackProducts: any[] = [];
	if (sliderProducts.length === 0 && innerRelated?.[0]) {
		// Even for fallback, prioritize noCrops
		const innerSource = innerNoCrops.length > 0 ? innerNoCrops : innerRelatedProducts;
		const innerToShow = innerSource.filter((x: any) => x.id !== currentProductId);
		
		if (innerToShow.length === 0) {
			fallbackProducts = [innerRelated[0]?.product];
		} else if (innerToShow.length > 0) {
			fallbackProducts = [...innerToShow, innerRelated[0]?.product];
		}
	}
	
	// Ultimate fallback to allProducts (generic product slider)
	if (sliderProducts.length === 0 && fallbackProducts.length === 0) {
		fallbackProducts = allProducts || [];
	}
	
	return {
		sliderProducts,
		fallbackProducts,
		source,
	};
}

/** PDP related slider card layout: `default` = equal grid/sizing, `zoom` = center-focus carousel. */
export type RelatedCardType = "default" | "zoom";

export function resolveRelatedCardType(
	brandsProduct: any
): RelatedCardType {
	const raw = brandsProduct?.relatedCardType;
	if (typeof raw === "string" && raw.toLowerCase() === "zoom") {
		return "zoom";
	}
	return "default";
}

export function resolveRelatedProductsButtonConfig(brandsProduct: any) {
	return (
		brandsProduct?.relatedProductsButtonConfig ??
		brandsProduct?.productDetailButtonConfig
	);
}

export function resolveRelatedProductsBrandLabel(brandsProduct: any) {
	if (
		brandsProduct?.addProductCustomBrandLabel &&
		brandsProduct?.customRelatedBrandLabel?.label
	) {
		return brandsProduct.customRelatedBrandLabel.label;
	}

	return undefined;
}
